/**
 * 運用詳細ページ
 *
 * 3タブ構成:
 * - 基本情報: 盤情報、データ分析ナビ、イベントログ
 * - 見える化データ: 8レジスタの計測値
 * - メンテ: 4つの動作回数
 *
 * 共通ヘッダー（タブ外）: 盤選択ドロップダウン + ステータスTag
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tag,
  Button,
  Form,
  Input,
  Space,
  Alert,
  message,
  Select,
  Modal,
  Tabs,
} from 'antd';
import { SwapOutlined, SettingOutlined } from '@ant-design/icons';
import {
  getPanelById,
  getPanelSettings,
  updatePanelSettings,
  getAllPanels,
  getPanelStatusLogs,
  getLatestVisualizationData,
  getLatestMaintenanceData,
  getLatestErrorData,
  type PanelBasicInfo,
  type PanelSettings,
  type PanelStatusLogEntry,
} from '@/features/panels/services/panelApiService';
import type { RegisterDisplayItem, MaintenanceDisplayItem, ErrorDisplayItem } from '@/features/panels/constants/panelDetailTab.constants';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { logger } from '@/shared/lib/logger';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { canEditPanel, canExportPanelData } from '@/features/auth/utils/permissionUtils';
import {
  PANEL_DETAIL_TAB_KEYS,
  PANEL_DETAIL_TAB_LABELS,
  type PanelDetailTabKey,
} from '@/features/panels/constants/panelDetailTab.constants';
import { BasicInfoTab } from '@/features/panels/components/BasicInfoTab';
import { VisualizationDataTab } from '@/features/panels/components/VisualizationDataTab';
import { MaintenanceTab } from '@/features/panels/components/MaintenanceTab';
import { ErrorTab } from '@/features/panels/components/ErrorTab';

// ========== 定数 ==========

const PANEL_DETAIL_CONSTANTS = {
  POLLING_INTERVAL_MS: 5000,
  PANEL_NAME_MAX_LENGTH: 100,
  DECIMAL_BASE: 10,
} as const;

const STATUS_CONFIG = {
  NORMAL: { color: 'green', text: '正常' },
  STOPPED: { color: 'default', text: '停止' },
  ERROR: { color: 'orange', text: '警告' },
  active: { color: 'green', text: '正常' },
  stopped: { color: 'default', text: '停止' },
  warning: { color: 'orange', text: '警告' },
} as const;

const STATUS_LOG_DISPLAY_LIMIT = 50;

// ========== コンポーネント ==========

export const PanelDetailPage: React.FC = () => {
  const { panelId } = useParams<{ panelId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { isMobile } = useResponsive();
  const { user } = useAuthStore();
  const hasEditPanelPermission = canEditPanel(user);
  const hasExportPanelDataPermission = canExportPanelData(user);

  // 共通state
  const [panelBasicInfo, setPanelBasicInfo] = useState<PanelBasicInfo | null>(null);
  const [panelSettings, setPanelSettings] = useState<PanelSettings | null>(null);
  const [statusLogs, setStatusLogs] = useState<readonly PanelStatusLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [availablePanels, setAvailablePanels] = useState<readonly PanelBasicInfo[]>([]);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);

  // 見える化データstate
  const [visualizationDisplayItems, setVisualizationDisplayItems] = useState<readonly RegisterDisplayItem[]>([]);
  const [isLoadingVisualization, setIsLoadingVisualization] = useState(false);
  const [visualizationErrorMessage, setVisualizationErrorMessage] = useState<string | null>(null);

  // メンテナンスデータstate
  const [maintenanceDisplayItems, setMaintenanceDisplayItems] = useState<readonly MaintenanceDisplayItem[]>([]);
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(false);
  const [maintenanceErrorMessage, setMaintenanceErrorMessage] = useState<string | null>(null);

  // エラーデータstate
  const [errorDisplayItems, setErrorDisplayItems] = useState<readonly ErrorDisplayItem[]>([]);
  const [isLoadingErrorData, setIsLoadingErrorData] = useState(false);
  const [errorDataErrorMessage, setErrorDataErrorMessage] = useState<string | null>(null);

  // 設定Modal state
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // タブstate
  const [activeTabKey, setActiveTabKey] = useState<PanelDetailTabKey>(PANEL_DETAIL_TAB_KEYS.BASIC_INFO);

  // URLパラメータから盤IDをプリミティブ値として取得
  const panelIdValue: number | null = useMemo(() => {
    if (panelId === undefined) {
      return null;
    }
    const parsedId = parseInt(panelId, PANEL_DETAIL_CONSTANTS.DECIMAL_BASE);
    return !isNaN(parsedId) ? parsedId : null;
  }, [panelId]);

  // ========== データ取得 ==========

  const validatePanelId = (id: number | null): boolean => {
    if (id === null) {
      setErrorMessage('無効な盤IDです');
      return false;
    }
    return true;
  };

  const fetchPanelBasicInfo = async (id: number): Promise<boolean> => {
    const basicInfo = await getPanelById({ panelId: id });
    if (!basicInfo) {
      setErrorMessage('指定された盤IDが存在しません');
      return false;
    }
    setPanelBasicInfo(basicInfo);
    return true;
  };

  const fetchPanelSettingsAndUpdateForm = async (id: number): Promise<void> => {
    const settings = await getPanelSettings({ panelId: id });
    if (settings) {
      setPanelSettings(settings);
      form.setFieldsValue({
        panelName: settings.panelName,
      });
    }
  };

  const fetchPanelStatusLogs = async (id: number): Promise<void> => {
    try {
      const result = await getPanelStatusLogs({
        panelId: id,
        limit: STATUS_LOG_DISPLAY_LIMIT,
      });
      setStatusLogs(result.logs);
    } catch (error) {
      logger.captureError(error, 'PanelDetailPage.fetchPanelStatusLogs');
    }
  };

  // アクセス可能な盤リストを取得
  useEffect(() => {
    const loadAvailablePanels = async (): Promise<void> => {
      try {
        setIsLoadingPanels(true);
        const panels = await getAllPanels();
        setAvailablePanels(panels);
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.loadAvailablePanels');
      } finally {
        setIsLoadingPanels(false);
      }
    };

    loadAvailablePanels();
  }, []);

  // 運用詳細データ取得
  useEffect(() => {
    const loadPanelDetailData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        if (!validatePanelId(panelIdValue)) {
          return;
        }

        const basicInfoSuccess = await fetchPanelBasicInfo(panelIdValue!);
        if (!basicInfoSuccess) {
          return;
        }

        await fetchPanelSettingsAndUpdateForm(panelIdValue!);
        await fetchPanelStatusLogs(panelIdValue!);
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.loadPanelDetailData');
        setErrorMessage('運用詳細データの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadPanelDetailData();
  }, [panelIdValue]);

  // リアルタイム更新ポーリング（ステータスTag用）
  useEffect(() => {
    if (panelIdValue === null) return;

    const interval = setInterval(async () => {
      try {
        const updatedBasicInfo = await getPanelById({ panelId: panelIdValue });
        if (updatedBasicInfo) {
          setPanelBasicInfo(updatedBasicInfo);
        }
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.pollingUpdatePanelInfo');
      }
    }, PANEL_DETAIL_CONSTANTS.POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [panelIdValue]);

  // リアルタイム更新ポーリング（イベントログ用）
  useEffect(() => {
    if (panelIdValue === null) return;

    const intervalId = setInterval(() => {
      fetchPanelStatusLogs(panelIdValue);
    }, PANEL_DETAIL_CONSTANTS.POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [panelIdValue]);

  // 見える化データ取得＋ポーリング（タブ表示中のみ）
  useEffect(() => {
    if (panelIdValue === null) return;
    if (activeTabKey !== PANEL_DETAIL_TAB_KEYS.VISUALIZATION_DATA) return;

    const fetchVisualizationData = async (): Promise<void> => {
      try {
        const items = await getLatestVisualizationData({ panelId: panelIdValue });
        setVisualizationDisplayItems(items);
        setVisualizationErrorMessage(null);
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.fetchVisualizationData');
        setVisualizationErrorMessage('見える化データの取得に失敗しました');
      }
    };

    setIsLoadingVisualization(true);
    fetchVisualizationData().finally(() => setIsLoadingVisualization(false));

    const intervalId = setInterval(fetchVisualizationData, PANEL_DETAIL_CONSTANTS.POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [panelIdValue, activeTabKey]);

  // メンテナンスデータ取得＋ポーリング（タブ表示中のみ）
  useEffect(() => {
    if (panelIdValue === null) return;
    if (activeTabKey !== PANEL_DETAIL_TAB_KEYS.MAINTENANCE) return;

    const fetchMaintenanceData = async (): Promise<void> => {
      try {
        const items = await getLatestMaintenanceData({ panelId: panelIdValue });
        setMaintenanceDisplayItems(items);
        setMaintenanceErrorMessage(null);
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.fetchMaintenanceData');
        setMaintenanceErrorMessage('メンテナンスデータの取得に失敗しました');
      }
    };

    setIsLoadingMaintenance(true);
    fetchMaintenanceData().finally(() => setIsLoadingMaintenance(false));

    const intervalId = setInterval(fetchMaintenanceData, PANEL_DETAIL_CONSTANTS.POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [panelIdValue, activeTabKey]);

  // エラーデータ取得＋ポーリング（タブ表示中のみ）
  useEffect(() => {
    if (panelIdValue === null) return;
    if (activeTabKey !== PANEL_DETAIL_TAB_KEYS.ERROR) return;

    const fetchErrorData = async (): Promise<void> => {
      try {
        const items = await getLatestErrorData({ panelId: panelIdValue });
        setErrorDisplayItems(items);
        setErrorDataErrorMessage(null);
      } catch (error) {
        logger.captureError(error, 'PanelDetailPage.fetchErrorData');
        setErrorDataErrorMessage('エラーデータの取得に失敗しました');
      }
    };

    setIsLoadingErrorData(true);
    fetchErrorData().finally(() => setIsLoadingErrorData(false));

    const intervalId = setInterval(fetchErrorData, PANEL_DETAIL_CONSTANTS.POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [panelIdValue, activeTabKey]);

  // ========== ハンドラ ==========

  const handlePanelChange = (newPanelId: number): void => {
    navigate(`/panels/${newPanelId}`);
  };

  const handleTabChange = (key: string): void => {
    setActiveTabKey(key as PanelDetailTabKey);
  };

  const handleOpenSettingsModal = (): void => {
    if (panelSettings) {
      form.setFieldsValue({
        panelName: panelSettings.panelName,
      });
    }
    setIsSettingsModalVisible(true);
  };

  const handleCloseSettingsModal = (): void => {
    if (!isSavingSettings) {
      setIsSettingsModalVisible(false);
      form.resetFields();
    }
  };

  const buildUpdateData = (
    values: { panelName: string }
  ): { panelName?: string } => {
    const updateData: { panelName?: string } = {};
    if (values.panelName !== panelSettings?.panelName) {
      updateData.panelName = values.panelName;
    }
    return updateData;
  };

  const updatePanelSettingsState = async (
    updateData: { panelName?: string }
  ): Promise<void> => {
    if (panelIdValue === null) return;

    const updatedSettings = await getPanelSettings({ panelId: panelIdValue });
    if (!updatedSettings) return;

    setPanelSettings(updatedSettings);

    if (!updateData.panelName) return;

    const updatedBasicInfo = await getPanelById({ panelId: panelIdValue });
    if (updatedBasicInfo) {
      setPanelBasicInfo(updatedBasicInfo);
    }
  };

  const handleSaveSettings = async (): Promise<void> => {
    if (panelIdValue === null) return;

    try {
      const values = await form.validateFields();
      setIsSavingSettings(true);

      const updateData = buildUpdateData(values);

      if (Object.keys(updateData).length === 0) {
        message.info('変更がありません');
        setIsSettingsModalVisible(false);
        return;
      }

      const response = await updatePanelSettings({ panelId: panelIdValue, settings: updateData });

      if (!response.success) {
        throw new Error('設定の更新に失敗しました');
      }

      message.success('設定を更新しました');
      await updatePanelSettingsState(updateData);
      setIsSettingsModalVisible(false);
    } catch (error) {
      logger.captureError(error, 'PanelDetailPage.handleSaveSettings');
      message.error('設定の保存に失敗しました');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // ========== ステータス設定 ==========

  const getStatusConfig = (): { readonly color: string; readonly text: string } => {
    if (!panelBasicInfo?.status) {
      return { color: 'default', text: '不明' };
    }
    const config = STATUS_CONFIG[panelBasicInfo.status as keyof typeof STATUS_CONFIG];
    return config ?? { color: 'default', text: '不明' };
  };

  // ========== ローディング/エラー ==========

  if (isLoading) {
    return (
      <div>
        <div>運用詳細データを読み込み中...</div>
      </div>
    );
  }

  if (errorMessage || !panelBasicInfo) {
    return (
      <div>
        <Alert
          message="盤が見つかりません"
          description={errorMessage ?? '指定された盤IDが存在しません。'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const statusConfig = getStatusConfig();

  // ========== タブ定義 ==========

  const tabItems = [
    {
      key: PANEL_DETAIL_TAB_KEYS.BASIC_INFO,
      label: PANEL_DETAIL_TAB_LABELS[PANEL_DETAIL_TAB_KEYS.BASIC_INFO],
      children: (
        <BasicInfoTab
          panelBasicInfo={panelBasicInfo}
          statusLogs={statusLogs}
          panelIdValue={panelIdValue!}
          isMobile={isMobile}
          hasExportPanelDataPermission={hasExportPanelDataPermission}
          hasEditPanelPermission={hasEditPanelPermission}
          isSettingsDisabled={!panelSettings}
          onOpenSettings={handleOpenSettingsModal}
          onNavigateToErrorTab={() => setActiveTabKey(PANEL_DETAIL_TAB_KEYS.ERROR)}
        />
      ),
    },
    {
      key: PANEL_DETAIL_TAB_KEYS.VISUALIZATION_DATA,
      label: PANEL_DETAIL_TAB_LABELS[PANEL_DETAIL_TAB_KEYS.VISUALIZATION_DATA],
      children: (
        <VisualizationDataTab
          isMobile={isMobile}
          visualizationDisplayItems={visualizationDisplayItems}
          isLoadingVisualization={isLoadingVisualization}
          visualizationErrorMessage={visualizationErrorMessage}
        />
      ),
    },
    {
      key: PANEL_DETAIL_TAB_KEYS.MAINTENANCE,
      label: PANEL_DETAIL_TAB_LABELS[PANEL_DETAIL_TAB_KEYS.MAINTENANCE],
      children: (
        <MaintenanceTab
          isMobile={isMobile}
          maintenanceDisplayItems={maintenanceDisplayItems}
          isLoadingMaintenance={isLoadingMaintenance}
          maintenanceErrorMessage={maintenanceErrorMessage}
        />
      ),
    },
    {
      key: PANEL_DETAIL_TAB_KEYS.ERROR,
      label: PANEL_DETAIL_TAB_LABELS[PANEL_DETAIL_TAB_KEYS.ERROR],
      children: (
        <ErrorTab
          isMobile={isMobile}
          errorDisplayItems={errorDisplayItems}
          isLoadingErrorData={isLoadingErrorData}
          errorDataErrorMessage={errorDataErrorMessage}
        />
      ),
    },
  ];

  // ========== レンダリング ==========

  return (
    <div>
      {/* 共通ヘッダー（タブ外） */}
      <div className="mb-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Select
            value={panelIdValue ?? undefined}
            onChange={handlePanelChange}
            loading={isLoadingPanels}
            className="flex-1 sm:flex-none sm:w-[300px]"
            placeholder="盤を選択"
            suffixIcon={<SwapOutlined />}
            size="large"
          >
            {availablePanels.map((panel) => (
              <Select.Option key={panel.id} value={panel.id}>
                {panel.panelName}
              </Select.Option>
            ))}
          </Select>
          <Tag
            color={statusConfig.color}
            className="m-0 shrink-0"
            style={{ fontSize: '14px', lineHeight: '30px', padding: '0 12px' }}
          >
            {statusConfig.text}
          </Tag>
        </div>
      </div>

      {/* タブ */}
      <Tabs
        activeKey={activeTabKey}
        onChange={handleTabChange}
        items={tabItems}
      />

      {/* 設定Modal（共通ヘッダーのボタンから開く） */}
      <Modal
        title={
          <Space>
            <SettingOutlined />
            設定
          </Space>
        }
        open={isSettingsModalVisible}
        onCancel={handleCloseSettingsModal}
        maskClosable={!isSavingSettings}
        closable={!isSavingSettings}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={handleCloseSettingsModal}
            disabled={isSavingSettings}
          >
            キャンセル
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveSettings}
            loading={isSavingSettings}
          >
            保存
          </Button>,
        ]}
        width={isMobile ? '90%' : 480}
      >
        <Form
          form={form}
          layout="vertical"
          className="py-4"
        >
          <Form.Item
            label="盤名"
            name="panelName"
            rules={[
              { required: true, message: '盤名は必須です' },
              { max: PANEL_DETAIL_CONSTANTS.PANEL_NAME_MAX_LENGTH, message: '盤名は100文字以内で入力してください' },
              { whitespace: true, message: '盤名を入力してください' },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="盤名を入力" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
